import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

// Stack of currently open modals so that only the top-most one reacts to
// Escape / Tab (a confirm dialog opened from inside an edit modal must not
// close both).
const openModals = [];

function getFocusable(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      element.getAttribute("aria-hidden") !== "true",
  );
}

/**
 * Accessibility primitives shared by every in-app modal:
 *  - moves focus into the modal when it opens (initialFocusRef, else the
 *    first focusable element, else the container itself)
 *  - traps Tab / Shift+Tab inside the modal
 *  - closes on Escape (top-most modal only)
 *  - restores focus to the previously focused element on close
 *
 * Usage:
 *   const dialogRef = useModalA11y({ open, onClose, initialFocusRef });
 *   <div ref={dialogRef} role="dialog" aria-modal="true" tabIndex={-1}>...</div>
 */
export default function useModalA11y({
  open,
  onClose,
  initialFocusRef,
  restoreFocus = true,
} = {}) {
  const containerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const initialFocusRefRef = useRef(initialFocusRef);

  useEffect(() => {
    onCloseRef.current = onClose;
    initialFocusRefRef.current = initialFocusRef;
  });

  useEffect(() => {
    if (!open) return undefined;

    const token = {};
    openModals.push(token);
    const isTopMost = () => openModals[openModals.length - 1] === token;

    const previouslyFocused =
      typeof document !== "undefined" ? document.activeElement : null;
    const container = containerRef.current;

    const focusInitial = () => {
      const preferred = initialFocusRefRef.current?.current;
      const target = preferred || getFocusable(container)[0] || container;
      if (target && typeof target.focus === "function") {
        target.focus({ preventScroll: true });
      }
    };
    focusInitial();

    const handleKeyDown = (event) => {
      if (!isTopMost()) return;
      if (event.key === "Escape" || event.key === "Esc") {
        event.preventDefault();
        event.stopPropagation();
        if (typeof onCloseRef.current === "function") onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !container) return;
      const focusable = getFocusable(container);
      if (focusable.length === 0) {
        event.preventDefault();
        if (container.focus) container.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const inside = container.contains(active);
      if (event.shiftKey) {
        if (!inside || active === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Keep focus inside when something outside the modal is clicked/focused.
    const handleFocusIn = (event) => {
      if (!isTopMost() || !container) return;
      if (container.contains(event.target)) return;
      const focusable = getFocusable(container);
      (focusable[0] || container).focus({ preventScroll: true });
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("focusin", handleFocusIn);
      const index = openModals.indexOf(token);
      if (index !== -1) openModals.splice(index, 1);
      if (
        restoreFocus &&
        previouslyFocused &&
        typeof previouslyFocused.focus === "function" &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open, restoreFocus]);

  return containerRef;
}
